import React, { useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import globalUrls from '../../globalUrls';
import { useSecurityStore } from '../../store/useSecurityStore';
import { StoreStatus } from '../../store/StoreStatus';

interface ProtectedRouteProps {
  /** Allowed roles for this route. If omitted, just requires authentication. */
  roles?: string[];
  /** Where to send the user when access is denied. */
  redirectTo?: string;
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  roles: allowedRoles,
  redirectTo = globalUrls.UNAUTHORIZED,
  children,
}) => {
  const location = useLocation();
  const { isLoggedIn, roles, storeStatus } = useSecurityStore();

  // Loading gate: if auth slice is busy, hold rendering
  if (storeStatus === StoreStatus.loading) {
    return <p>Loading...</p>;
  }

  // Not authenticated — bounce to login (preserve intended path)
  if (!isLoggedIn) {
    return (
      <Navigate
        to={`${globalUrls.CLIENT_LOGIN}?redirect-url=${encodeURIComponent(location.pathname.slice(1))}`}
        replace
      />
    );
  }

  // If roles are required, ensure user has at least one of them
  const hasAccess = useMemo(() => {
    if (!allowedRoles || allowedRoles.length === 0) return true;
    return roles?.some(r => allowedRoles.includes(r));
  }, [allowedRoles, roles]);

  return hasAccess ? <>{children}</> : <Navigate to={redirectTo} replace />;
};

export default ProtectedRoute;
